import { useState } from 'react';
import { Flag, MessageSquare, Moon, Sun } from 'lucide-react';
import type { Basis, Person, Priority, Status, Task } from '../api.ts';
import { basisLabel, fmtDate, isOverdue, priorityLabel, statusLabel } from '../util.ts';

// Ops, the assistant: one name and one mark everywhere. The model behind it (Gemini) only shows in the fine print.
export const OPS = 'Ops';
export function OpsMark({ size = 14, className = '', ...a }: { size?: number; className?: string; 'aria-label'?: string; 'aria-hidden'?: boolean | 'true' }) {
  return <span className={`ops-mark ${className}`} style={{ width: size + 4, height: size + 4, fontSize: size * 0.72 }} role={a['aria-label'] ? 'img' : undefined}
    aria-label={a['aria-label']} aria-hidden={a['aria-label'] ? undefined : true} title={a['aria-label']}>✦</span>;
}

export function Avatar({ email, people, size = 24 }: { email: string | null; people: Person[]; size?: number }) {
  const p = people.find((x) => x.email === email);
  if (email === 'operator@agents.gbsoft.dev') return <span className="avatar agent" style={{ width: size, height: size }} title={OPS}>✦</span>;
  const label = p?.name || email?.split('@')[0] || '?';
  if (p?.picture) return <img className="avatar" src={p.picture} alt="" width={size} height={size} referrerPolicy="no-referrer" title={label} />;
  return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.42 }} title={label}>{email ? label.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() : '–'}</span>;
}

// A flag in the priority's color; an outline when there is none. With onChange it is also the picker.
export function PriorityFlag({ p, onChange, size = 14 }: { p: Priority | null | undefined; onChange?: (p: Priority | null) => void; size?: number }) {
  const label = p ? `${priorityLabel[p]} priority` : 'No priority';
  const flag = <Flag size={size} className={`prio p-${p ?? 'none'}`} fill={p ? 'currentColor' : 'none'} aria-hidden />;
  if (!onChange) return p ? <span title={label} role="img" aria-label={label}>{flag}</span> : null;
  return (
    <label className="prio-pick" title={label}>{flag}
      <select value={p ?? ''} onChange={(e) => onChange((e.target.value || null) as Priority | null)} aria-label="Priority">
        <option value="">No priority</option>{(Object.keys(priorityLabel) as Priority[]).map((k) => <option key={k} value={k}>{priorityLabel[k]}</option>)}</select>
    </label>
  );
}

export const StatusPill = ({ status }: { status: Status }) => <span className={`pill s-${status}`}>{statusLabel[status]}</span>;

export function DateText({ t }: { t: Pick<Task, 'start_date' | 'end_date' | 'date_basis' | 'status' | 'done_on' | 'kind'> }) {
  if (t.status === 'done' && t.done_on) return <span className="date done">Done {fmtDate(t.done_on)}</span>;
  if (t.date_basis === 'unknown') return <span className="tbd">TBD · Unscheduled</span>;
  const range = t.start_date && t.end_date && t.start_date !== t.end_date ? `${fmtDate(t.start_date)} – ${fmtDate(t.end_date)}` : fmtDate(t.end_date ?? t.start_date);
  return <span className={`date b-${t.date_basis}${isOverdue(t) ? ' late' : ''}`} title={basisLabel[t.date_basis as Basis]}>{range}<i>{basisLabel[t.date_basis as Basis]}</i></span>;
}

// How many comments a task has; nothing when it has none.
export function CommentCount({ n }: { n?: number }) {
  return n ? <span className="ccount" title={`${n} comment${n === 1 ? '' : 's'}`}><MessageSquare size={11} aria-hidden />{n}</span> : null;
}

export const Empty = ({ children }: { children: React.ReactNode }) => <p className="empty">{children}</p>;
export const Err = ({ e }: { e: unknown }) => (e ? <p className="err" role="alert">{e instanceof Error ? e.message : String(e)}</p> : null);

// Corporate light is the default; dark (slate) is a per-browser choice.
const savedTheme = (() => { try { return localStorage.getItem('gbops-theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; } })();
document.documentElement.dataset.theme = savedTheme;

export function ThemeToggle() {
  const [theme, setTheme] = useState(savedTheme);
  const flip = () => { const t = theme === 'dark' ? 'light' : 'dark'; setTheme(t); document.documentElement.dataset.theme = t; localStorage.setItem('gbops-theme', t); };
  return <button className="theme-toggle" onClick={flip} aria-label="Switch theme">{theme === 'dark' ? <Sun size={13} aria-hidden /> : <Moon size={13} aria-hidden />} {theme === 'dark' ? 'Light mode' : 'Dark mode'}</button>;
}
